// src/pages/User/UserCompanyDocuments.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Sidebar from '../components/Global/sidebar'; // ✅ Import Reusable Sidebar
import { authenticatedFetch, getAuthHeaders, clearStoredCredentials } from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://fslapi.devformula.com/api';

function UserCompanyDocuments() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);

  // ✅ Get Company ID from session
  const getCompanyId = () => {
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    return session?.companyId || session?.CompanyId || session?.sessionId || null;
  };

  // ✅ Fetch ONLY documents - matches backend: { SessionId: "123" }
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const companyId = getCompanyId();
      
      if (!companyId) {
        throw new Error('Company ID not found in session');
      }

      // ✅ Backend expects SessionId (string), not companyId (number)
      const result = await authenticatedFetch(
        `${API_BASE}/HMO/get-company-documents`,
        'POST',
        { SessionId: String(companyId) }  // ✅ Correct payload
      );
      
      if (result?.status === 'success' && Array.isArray(result.data)) {
        setDocuments(result.data);
      } else {
        throw new Error(result?.message || 'Failed to load documents');
      }
    } catch (error) {
      console.error('💥 Fetch error:', error);
      toast.error(error.message?.includes('Session expired') 
        ? 'Session expired. Please log in again.' 
        : 'Could not load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ✅ Auth check + fetch on mount
  useEffect(() => {
    if (!localStorage.getItem('userSession')) {
      toast.error('Please log in to view documents');
      navigate('/login', { replace: true });
      return;
    }
    fetchDocuments();
  }, [fetchDocuments, navigate]);

  // ✅ View Document (Open in New Tab)
  const handleViewDocument = async (fileName) => {
    if (!fileName) {
      toast.error('No file available to view');
      return;
    }

    try {
      // 1. Get Auth Headers
      const session = JSON.parse(localStorage.getItem('userSession') || '{}');
      const creds = JSON.parse(localStorage.getItem('fsl_auth_credentials') || '{}');
      
      const headers = {
        'AuthorizationKey': session.authorizationKey || creds.authorizationKey || '',
        'Token': session.token || creds.token || '',
        'SecretKey': session.secretKey || creds.secretKey || '',
      };

      // 2. Fetch File with Auth Headers
      const response = await fetch(`${API_BASE}/HMO/get-company-document/${encodeURIComponent(fileName)}`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please log in again.');
          clearStoredCredentials();
          navigate('/login');
          return;
        }
        throw new Error('Failed to load document');
      }

      // 3. Convert to Blob and Open
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Cleanup memory after 30 seconds
      setTimeout(() => URL.revokeObjectURL(url), 30000);

    } catch (error) {
      console.error('View error:', error);
      toast.error('Could not open document');
    }
  };

  // ✅ Download Document (Force Save)
  const handleDownloadDocument = async (fileName) => {
    if (!fileName) {
      toast.error('No file available to download');
      return;
    }

    try {
      // 1. Get Auth Headers
      const session = JSON.parse(localStorage.getItem('userSession') || '{}');
      const creds = JSON.parse(localStorage.getItem('fsl_auth_credentials') || '{}');
      
      const headers = {
        'AuthorizationKey': session.authorizationKey || creds.authorizationKey || '',
        'Token': session.token || creds.token || '',
        'SecretKey': session.secretKey || creds.secretKey || '',
      };

      // 2. Fetch File with Auth Headers
      const response = await fetch(`${API_BASE}/HMO/get-company-document/${encodeURIComponent(fileName)}`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please log in again.');
          clearStoredCredentials();
          navigate('/login');
          return;
        }
        throw new Error('Failed to download document');
      }

      // 3. Convert to Blob and Trigger Download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName; // Suggests filename to browser
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup memory
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success('Download started');

    } catch (error) {
      console.error('Download error:', error);
      toast.error('Could not download document');
    }
  };

  // ✅ Logout
  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    localStorage.removeItem('fsl_session');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  // ✅ Nav handler
  const handleNavClick = (path, e) => {
    e?.preventDefault();
    if (path) navigate(path);
  };

  // ✅ Get file icon
  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.')?.pop()?.toLowerCase();
    if (ext === 'pdf') return { icon: 'fa-file-pdf', color: 'text-danger' };
    if (['doc', 'docx'].includes(ext)) return { icon: 'fa-file-word', color: 'text-primary' };
    if (['xls', 'xlsx'].includes(ext)) return { icon: 'fa-file-excel', color: 'text-success' };
    if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return { icon: 'fa-file-image', color: 'text-warning' };
    return { icon: 'fa-file', color: 'text-secondary' };
  };

  // ✅ Format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <div className="user-documents-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* ✅ Left Sidebar (Using Reusable Component) */}
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        onNavClick={handleNavClick} 
        onLogout={handleLogout} 
      />

      {/* ✅ MAIN CONTENT */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)}><i className="fas fa-bars"></i></button>
            <div className="page-title">
              <i className="fas fa-arrow-left me-2" style={{ cursor: 'pointer' }} onClick={() => navigate(-1)}></i>
              <h4 className="mb-0">Company Documents</h4>
            </div>
          </div>
          <div className="header-right">
            <button className="header-icon" onClick={fetchDocuments} disabled={loading} title="Refresh">
              <i className={`fas fa-sync${loading ? '-alt fa-spin' : ''}`}></i>
            </button>
            <button className="header-icon"><i className="fas fa-bell"></i><span className="badge">3</span></button>
            <div className="user-avatar"><span>U</span></div>
          </div>
        </header>

        <div className="content-wrapper">
          {/* ✅ Simple Documents Table */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" style={{ width: '32px', height: '32px' }}></div>
                <p className="mt-3 text-muted">Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-folder-open fa-2x mb-3 opacity-25"></i>
                <h5>No documents found</h5>
                <p style={{ fontSize: '14px' }}>Your company documents will appear here once uploaded.</p>
              </div>
            ) : (
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151', width: '50px' }}>#</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Document</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Type</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Date</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151', width: '180px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => {
                    const fileIcon = getFileIcon(doc.documentFile);
                    return (
                      <tr key={doc.documentId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td className="py-3 px-4" style={{ fontSize: '13px', color: '#6b7280' }}>{index + 1}</td>
                        <td className="py-3 px-4">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <i className={`fas ${fileIcon.icon} ${fileIcon.color} fa-lg`}></i>
                            <div>
                              <p style={{ margin: '0 0 2px', fontWeight: '500', fontSize: '14px', color: '#1f2937' }}>
                                {doc.documentTitle || 'Untitled'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="badge" style={{ 
                            padding: '4px 12px', borderRadius: '12px', fontSize: '12px',
                            backgroundColor: '#f3f4f6', color: '#374151'
                          }}>
                            {doc.documentType?.toUpperCase() || 'DOC'}
                          </span>
                        </td>
                        <td className="py-3 px-4" style={{ fontSize: '13px', color: '#6b7280' }}>
                          {doc.createdDate ? new Date(doc.createdDate).toLocaleDateString('en-NG') : 'Unknown'}
                        </td>
                        <td className="py-3 px-4">
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button 
                              onClick={() => handleViewDocument(doc.documentFile)}
                              title="View"
                              style={{ 
                                padding: '6px 12px', background: '#e0e7ff', color: '#4338ca',
                                border: 'none', borderRadius: '6px', fontSize: '12px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '4px'
                              }}
                            >
                              <i className="fas fa-eye"></i> View
                            </button>
                            <button 
                              onClick={() => handleDownloadDocument(doc.documentFile)}
                              title="Download"
                              style={{ 
                                padding: '6px 12px', background: '#F89423', color: 'white',
                                border: 'none', borderRadius: '6px', fontSize: '12px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '4px'
                              }}
                            >
                              <i className="fas fa-download"></i> Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Results count */}
          {documents.length > 0 && (
            <div style={{ marginTop: '16px', fontSize: '13px', color: '#6b7280', textAlign: 'right' }}>
              Showing {documents.length} document{documents.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* ✅ CSS (Sidebar styles removed since they are in Sidebar.css now) */}
      <style>{`
        .user-documents-page { display: flex; min-height: 100vh; background-color: #f8f9fa; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .top-header { background: white; padding: 15px 30px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .page-title { display: flex; align-items: center; color: #1f2937; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .header-icon { width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; position: relative; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; }
        .header-icon:hover { background: #e5e7eb; color: #F89423; }
        .header-icon .badge { position: absolute; top: 5px; right: 5px; background: #F89423; color: white; font-size: 10px; padding: 2px 5px; border-radius: 10px; }
        .user-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, #F89423 0%, #F89423 100%); color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; cursor: pointer; }
        .content-wrapper { padding: 30px; flex: 1; }
        .table th { background-color: #f9fafb; border-bottom: 1px solid #e5e7eb; font-weight: 600; font-size: 13px; color: #374151; }
        .table td { vertical-align: middle; border-bottom: 1px solid #f3f4f6; padding: 12px 16px; }
        .badge { display: inline-block; font-weight: 500; }
        .spinner-border { display: inline-block; width: 2rem; height: 2rem; border: 0.15em solid currentColor; border-right-color: transparent; border-radius: 50%; animation: spinner-border 0.75s linear infinite; }
        @keyframes spinner-border { to { transform: rotate(360deg); } }
        
        @media (max-width: 992px) { 
          .main-content { margin-left: 0; } 
        }
        @media (max-width: 768px) { 
          .content-wrapper { padding: 15px; } 
          .top-header { padding: 15px; } 
          .table th, .table td { padding: 10px 12px; font-size: 13px; } 
        }
      `}</style>
    </div>
  );
}

export default UserCompanyDocuments;